import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import Select from "react-select";
import { styles } from "../../../utils/helper";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { setCourses } from "../../../store/actions/CourseAction";
import axios from "axios";
import { API_URL } from "../../../config";
import { toast } from "react-toastify";

const specializationsCache = {};

const RequirementForm = ({
  formik,
  onSpecializationsChange,
  setIsFileUploaded,
  setIsAdditionalFileUploaded,
  drive,
}) => {
  const [courseSpecializations, setCourseSpecializations] = useState({});
  const [isUploadingJobDescription, setIsUploadingJobDescription] =
    useState(false);
  const [isUploadingAdditionalFile, setIsUploadingAdditionalFile] =
    useState(false);
  const [isLoadingSpecializations, setIsLoadingSpecializations] =
    useState(false);
  const courses = useSelector((state) => state.courses.courses);
  const collegeId = useSelector((state) => state.college_id.college_id);
  const [jobDescriptionFile, setJobDescriptionFile] = useState(
    drive?.job_description_file_name
      ? {
          file_name: drive?.job_description_file_name,
          file_path: drive?.job_description_file_url,
        }
      : null
  );
  const [additionalDocumentFile, setAdditionalDocumentFile] = useState(
    drive?.additional_document_file_name
      ? {
          file_name: drive?.additional_document_file_name,
          file_path: drive?.additional_document_file_url,
        }
      : null
  );
  const dispatch = useDispatch();
  const passoutYearOptions = [
    moment().year() - 2,
    moment().year() - 1,
    moment().year(),
    moment().year() + 1,
    moment().year() + 2,
  ].map((item) => ({ value: item, label: item }));

  const showFieldError = (fieldName) =>
    formik.touched[fieldName] && formik.errors[fieldName];

  const getSpecializations = async (id) => {
    // Check if we already have this specialization cached
    if (specializationsCache[id]) {
      return specializationsCache[id];
    }

    setIsLoadingSpecializations(true);
    const userDetails = JSON.parse(localStorage.getItem("userDetails"));
    const authToken = userDetails?.data?.auth_token;

    const course = courses.find((course) => course.course_master_id === id);
    if (!course) {
      return [];
    }
    const payload = {
      college_id: collegeId,
      course_id: course?.college_course_id,
    };
    try {
      const response = await axios.get(
        `${API_URL}/api/freshment_portal_apis/GenericAPI/get_course_spealization_list`,
        {
          params: payload,
          headers: {
            Authorization: authToken,
          },
        }
      );
      // Store in our persistent cache
      specializationsCache[id] = response.data.data ?? [];
      return specializationsCache[id];
    } catch (error) {
      console.error("Error fetching specializations:", error);
      return [];
    } finally {
      setIsLoadingSpecializations(false);
    }
  };

  // New function to handle course-specific specialization changes
  const handleCourseSpecializationChange = (
    courseId,
    selectedSpecializations
  ) => {
    const newCourseSpecializations = {
      ...formik.values.courseSpecializations,
      [courseId]: selectedSpecializations || [],
    };

    formik.setFieldValue("courseSpecializations", newCourseSpecializations);

    // Also update the flattened selectedSpecializations for backward compatibility
    const allSelectedSpecializations = Object.values(
      newCourseSpecializations
    ).flat();
    formik.setFieldValue("selectedSpecializations", allSelectedSpecializations);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      formik.setFieldValue("files", selectedFile);
    }
  };

  const handleRemoveFile = () => {
    // formik.setFieldValue("files", null);
    setJobDescriptionFile(null);
    formik.setFieldValue("job_description_file_name", "");
    setIsFileUploaded(false);
  };

  const handleRemoveAdditionalFile = () => {
    // formik.setFieldValue("additionalFile", null);
    setAdditionalDocumentFile(null);
    formik.setFieldValue("additional_document_file_name", "");
    setIsAdditionalFileUploaded(false);
  };

  useEffect(() => {
    const storedCourses = localStorage.getItem("courses");
    if (storedCourses) {
      dispatch(setCourses(JSON.parse(storedCourses)));
    }
  }, [dispatch]);

  useEffect(() => {
    const fetchSpecializationsForSelectedCourses = async () => {
      if (
        formik.values.selectedCourses &&
        formik.values.selectedCourses.length > 0
      ) {
        const newCourseSpecializations = {};

        // Fetch specializations for each course
        for (const courseId of formik.values.selectedCourses) {
          if (!specializationsCache[courseId]) {
            await getSpecializations(courseId);
          }
          newCourseSpecializations[courseId] =
            specializationsCache[courseId] || [];
        }

        setCourseSpecializations(newCourseSpecializations);

        // Clean up courseSpecializations - only keep specializations for selected courses
        const currentCourseSpecializations =
          formik.values.courseSpecializations || {};
        const cleanedCourseSpecializations = {};

        // Only include specializations for currently selected courses
        formik.values.selectedCourses.forEach((courseId) => {
          cleanedCourseSpecializations[courseId] =
            currentCourseSpecializations[courseId] || [];
        });

        // Handle edit mode - map selectedSpecializations back to courses
        if (drive && formik.values.selectedSpecializations?.length > 0) {
          const editModeCourseSpecializations = {};

          // For each selected course, find which specializations belong to it
          for (const courseId of formik.values.selectedCourses) {
            const courseSpecs = newCourseSpecializations[courseId] || [];

            // Find which of the selected specializations belong to this course
            const courseSelectedSpecs =
              formik.values.selectedSpecializations.filter((specId) =>
                courseSpecs.some(
                  (spec) => spec.specilization_master_id === specId
                )
              );

            if (courseSelectedSpecs.length > 0) {
              editModeCourseSpecializations[courseId] = courseSelectedSpecs;
            }
          }

          // Set the mapped courseSpecializations
          formik.setFieldValue(
            "courseSpecializations",
            editModeCourseSpecializations
          );

          // Update flattened selectedSpecializations with only valid specializations
          const allSelectedSpecializations = Object.values(
            editModeCourseSpecializations
          ).flat();
          formik.setFieldValue(
            "selectedSpecializations",
            allSelectedSpecializations
          );
        } else {
          // Set the cleaned courseSpecializations
          formik.setFieldValue(
            "courseSpecializations",
            cleanedCourseSpecializations
          );

          // Update flattened selectedSpecializations
          const allSelectedSpecializations = Object.values(
            cleanedCourseSpecializations
          ).flat();
          formik.setFieldValue(
            "selectedSpecializations",
            allSelectedSpecializations
          );
        }
      } else {
        // No courses selected - clear everything
        setCourseSpecializations({});
        formik.setFieldValue("courseSpecializations", {});
        formik.setFieldValue("selectedSpecializations", []);
      }
    };

    fetchSpecializationsForSelectedCourses();
  }, [formik.values.selectedCourses, courses]);

  useEffect(() => {
    // Pass all specializations to parent component for display purposes
    const allSpecializations = Object.values(courseSpecializations).flat();
    if (onSpecializationsChange) {
      onSpecializationsChange(allSpecializations);
    }
  }, [courseSpecializations, onSpecializationsChange]);

  const handleUploadFile = async (fieldName) => {
    try {
      if (fieldName === "files") setIsUploadingJobDescription(true);
      if (fieldName === "additionalFile") setIsUploadingAdditionalFile(true);

      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const authToken = userDetails?.data?.auth_token;
      const file = formik.values[fieldName];

      const size = file.size;

      if (size > 2 * 1024 * 1024) {
        toast.error("Please upload a file smaller than 2MB");
        return null;
      }

      const isValidFile =
        file.name.toLowerCase().endsWith("pdf") ||
        file.name.toLowerCase().endsWith("doc") ||
        file.name.toLowerCase().endsWith("docx");
      if (!isValidFile) {
        toast.error("Please upload a valid file (PDF or DOC/DOCX only)");
        return null;
      }
      const formData = new FormData();
      formData.append("college_id", collegeId);
      formData.append("file", file);
      formData.append("display_name", file.name?.split(".")[0]);
      const response = await axios.post(
        `${API_URL}/api/freshment_portal_apis/CampusDrive/upload_campus_drive_file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.success) {
        if (fieldName === "files") {
          setIsFileUploaded(true);
          setJobDescriptionFile(response.data.data);
        }
        if (fieldName === "additionalFile") {
          setIsAdditionalFileUploaded(true);
          setAdditionalDocumentFile(response.data.data);
        }
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error("Error uploading file:", error);
      return null;
    } finally {
      if (fieldName === "files") setIsUploadingJobDescription(false);
      if (fieldName === "additionalFile") setIsUploadingAdditionalFile(false);
    }
  };

  // Get course name by ID
  const getCourseName = (courseId) => {
    const course = courses.find((c) => c.course_master_id === courseId);
    return course ? course.name : `Course ${courseId}`;
  };

  return (
    <>
      <h5 className="mb-4">Requirements</h5>
      <Row className="g-3 align-items-start">
        <Col md={12}>
          <div className="form-group">
            <label htmlFor="jobDescription" className="form-label fw-semibold">
              Job Description (Minimum 200 Characters){" "}
              <span className="text-danger">*</span>
            </label>
            <textarea
              name="jobDescription"
              rows={4}
              value={formik.values?.jobDescription}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onFocus={formik.handleFocus}
              style={{ height: "100px" }}
              className={`form-control ${
                showFieldError("jobDescription") ? "is-invalid" : ""
              }`}
              placeholder="Describe job responsibilities and requirements"
            />
            <div className="d-flex justify-content-between">
              <div>
                {showFieldError("jobDescription") && (
                  <p className="text-danger mt-1">
                    {formik.errors.jobDescription}
                  </p>
                )}
              </div>
              <small className="text-muted d-block text-end mt-1">
                {formik.values.jobDescription?.length || 0}/200 characters
              </small>
            </div>
          </div>
        </Col>

        <Col md={12}>
          <div className="form-group">
            <label
              htmlFor="placementEligibilityCriteria"
              className="form-label fw-semibold"
            >
              Placement Eligibility Criteria (Minimum 100 Characters){" "}
              <span className="text-danger">*</span>
            </label>
            <textarea
              name="placementEligibilityCriteria"
              rows={4}
              value={formik.values?.placementEligibilityCriteria}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onFocus={formik.handleFocus}
              style={{ height: "100px" }}
              className={`form-control ${
                showFieldError("placementEligibilityCriteria")
                  ? "is-invalid"
                  : ""
              }`}
              placeholder="Specify academic criteria, CGPA requirements, etc."
            />
            <div className="d-flex justify-content-between">
              <div>
                {showFieldError("placementEligibilityCriteria") && (
                  <p className="text-danger mt-1">
                    {formik.errors.placementEligibilityCriteria}
                  </p>
                )}
              </div>
              <small className="text-muted d-block text-end mt-1">
                {formik.values.placementEligibilityCriteria?.length || 0}
                /100 characters
              </small>
            </div>
          </div>
        </Col>

        <Col md={12}>
          <div className="form-group">
            <label htmlFor="interviewRounds" className="form-label fw-semibold">
              Interview Rounds (Minimum 20 Characters){" "}
              <span className="text-danger">*</span>
            </label>
            <textarea
              id="interviewRounds"
              name="interviewRounds"
              rows={4}
              value={formik.values?.interviewRounds}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              style={{ height: "100px" }}
              className={`form-control ${
                showFieldError("interviewRounds") ? "is-invalid" : ""
              }`}
              placeholder="List all selection rounds (e.g., Online test, Technical interview, HR)"
            />
            <div className="d-flex justify-content-between">
              <div>
                {showFieldError("interviewRounds") && (
                  <p className="text-danger mt-1">
                    {formik.errors.interviewRounds}
                  </p>
                )}
              </div>
              <small className="text-muted d-block text-end mt-1">
                {formik.values.interviewRounds?.length || 0}/20 characters
              </small>
            </div>
          </div>
        </Col>

        <Col md={12}>
          <hr />
          <h6 className="mb-3">Additional Details</h6>
        </Col>

        <Col md={12}>
          <div className="form-group">
            <label
              htmlFor="termsAndConditions"
              className="form-label fw-semibold"
            >
              Any Other Terms and Conditions (Minimum 100 Characters)
            </label>
            <textarea
              name="termsAndConditions"
              rows={4}
              value={formik.values?.termsAndConditions}
              onChange={formik.handleChange}
              style={{ height: "100px" }}
              className={`form-control ${
                showFieldError("termsAndConditions") ? "is-invalid" : ""
              }`}
              placeholder="Additional information, service agreements, etc."
              onBlur={formik.handleBlur}
            />
            <div className="d-flex justify-content-between">
              <div>
                {showFieldError("termsAndConditions") && (
                  <p className="text-danger mt-1">
                    {formik.errors.termsAndConditions}
                  </p>
                )}
              </div>
              <small className="text-muted d-block text-end mt-1">
                {formik.values.termsAndConditions?.length || 0}/100 characters
              </small>
            </div>
          </div>
        </Col>

        <Col md={12}>
          <hr />
          <h6 className="mb-3">Eligibility</h6>
        </Col>

        <Col md={12}>
          <div className="form-group mb-3">
            <label className="form-label fw-semibold">
              Select Courses <span className="text-danger">*</span>
            </label>
            <Select
              isMulti
              name="selectedCourses"
              options={
                courses?.map((course) => ({
                  label: course.name,
                  value: course.course_master_id,
                })) || []
              }
              className={`basic-multi-select ${
                showFieldError("selectedCourses") ? "is-invalid" : ""
              }`}
              styles={styles}
              placeholder="Select courses"
              onChange={(selectedOptions) => {
                const values = selectedOptions
                  ? selectedOptions.map((option) => {
                      return option.value;
                    })
                  : [];

                formik.setFieldValue("selectedCourses", values);

                if (values.length === 0) {
                  setCourseSpecializations({});
                  formik.setFieldValue("courseSpecializations", {});
                  formik.setFieldValue("selectedSpecializations", []);
                }
              }}
              onBlur={formik.handleBlur}
              value={
                courses
                  ?.map((course) => ({
                    label: course.name,
                    value: course.course_master_id,
                  }))
                  .filter((course) =>
                    formik.values?.selectedCourses?.includes(course.value)
                  ) || []
              }
            />
            {showFieldError("selectedCourses") && (
              <div className="text-danger mt-1">
                {formik.errors.selectedCourses}
              </div>
            )}
          </div>
        </Col>

        {/* Render separate specialization dropdowns for each selected course */}
        {formik.values.selectedCourses &&
          formik.values.selectedCourses.length > 0 && (
            <Col md={12} className="m-0">
              {/* <h6 className="mb-3">Course Specializations</h6> */}
              <Row>
                {formik.values.selectedCourses.map((courseId) => {
                  const courseSpecs = courseSpecializations[courseId] || [];
                  const selectedSpecs =
                    (formik.values.courseSpecializations &&
                      formik.values.courseSpecializations[courseId]) ||
                    [];

                  if (courseSpecs.length === 0) return null;

                  return (
                    <Col md={6} key={courseId} className="mb-3">
                      <div className="form-group">
                        <label className="form-label fw-semibold">
                          {getCourseName(courseId)} - Specializations
                        </label>
                        <Select
                          isMulti
                          isLoading={isLoadingSpecializations}
                          name={`courseSpecializations`}
                          options={courseSpecs.map((specialization) => ({
                            label: specialization.name,
                            value: specialization.specilization_master_id,
                          }))}
                          className="basic-multi-select"
                          styles={styles}
                          placeholder={
                            isLoadingSpecializations
                              ? "Loading specializations..."
                              : "Select specializations"
                          }
                          onChange={(selectedOptions) => {
                            const values = selectedOptions
                              ? selectedOptions.map((option) => option.value)
                              : [];
                            handleCourseSpecializationChange(courseId, values);
                          }}
                          value={
                            courseSpecs
                              .map((specialization) => ({
                                label: specialization.name,
                                value: specialization.specilization_master_id,
                              }))
                              .filter((specialization) =>
                                selectedSpecs.includes(specialization.value)
                              ) || []
                          }
                        />
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Col>
          )}

        <Col md={12}>
          <div className="form-group mb-3">
            <label className="form-label fw-semibold">
              Select Passout Year <span className="text-danger">*</span>
            </label>
            <Select
              isMulti
              name="selectedPassoutYears"
              options={passoutYearOptions}
              className="basic-multi-select"
              styles={styles}
              placeholder="Select passout years"
              onChange={(selectedOptions) => {
                const values = selectedOptions
                  ? selectedOptions.map((option) => option.value)
                  : [];
                formik.setFieldValue("selectedPassoutYears", values);
              }}
              onBlur={formik.handleBlur}
              value={
                formik.values?.selectedPassoutYears?.map((year) => ({
                  value: year,
                  label: year,
                })) || []
              }
            />
            {showFieldError("selectedPassoutYears") && (
              <div className="text-danger mt-1">
                {formik.errors.selectedPassoutYears}
              </div>
            )}
          </div>
        </Col>

        {/* File upload sections remain the same */}
        <Col md={6}>
          <div className="card mb-3 border border-black">
            <div className="card-header">
              <h5 className="mb-0">Job Description File</h5>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label fw-semibold">
                  Upload Job Description File (Max 2MB){" "}
                </label>
                <div className="file-upload-container">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <label
                        htmlFor="file-upload"
                        className="btn btn-outline-primary"
                        style={{ cursor: "pointer", marginBottom: 0 }}
                      >
                        <i className="fa fa-cloud-upload me-2"></i>
                        Select Files
                      </label>
                      <input
                        id="file-upload"
                        type="file"
                        onBlur={formik.handleBlur}
                        onChange={(e) => handleFileChange(e)}
                        name="files"
                        className="d-none"
                        accept=".pdf,.doc,.docx"
                      />
                    </div>
                    <div className="small text-muted">
                      Supported formats: PDF, DOC, DOCX
                    </div>
                  </div>

                  {formik.values?.files && (
                    <div className="selected-files mt-3">
                      <div
                        className="d-flex align-items-center p-2 border rounded mb-2"
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        <i
                          className={`fa fa-${
                            formik.values.files.name
                              .toLowerCase()
                              .endsWith("pdf")
                              ? "file-pdf-o"
                              : "file-word-o"
                          } me-2 text-${
                            formik.values.files.name
                              .toLowerCase()
                              .endsWith("pdf")
                              ? "danger"
                              : "primary"
                          }`}
                        ></i>
                        <div className="flex-grow-1 ms-2 overflow-hidden">
                          <div className="text-truncate">
                            {formik.values.files.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0 ms-2"
                          onClick={() => {
                            formik.setFieldValue("files", null);
                            const fileInput =
                              document.getElementById("file-upload");
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          style={{ minWidth: "24px" }}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                      <button
                        type="button"
                        disabled={isUploadingJobDescription}
                        className="btn btn-primary mt-2"
                        onClick={async () => {
                          const file = await handleUploadFile("files");
                          if (file) {
                            formik.setFieldValue(
                              "job_description_file_name",
                              file?.file_name
                            );
                            toast.success("File uploaded successfully");
                          }
                        }}
                      >
                        {isUploadingJobDescription ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-upload me-2"></i>
                            Upload
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {showFieldError("files") && (
                    <div className="text-danger mt-2">
                      {formik.errors.files}
                    </div>
                  )}

                  {!showFieldError("files") &&
                    formik.values.files &&
                    formik.errors.job_description_file_name && (
                      <div className="text-danger mt-2">
                        {formik.errors.job_description_file_name}
                      </div>
                    )}
                </div>
              </div>
              {(jobDescriptionFile ||
                formik.values.job_description_file_name) && (
                <div className="form-group mt-2">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <a
                        download
                        className="text-decoration-none text-primary"
                        href={
                          jobDescriptionFile?.file_path ||
                          formik.values.job_description_file_url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fa fa-file me-2"></i>
                        {jobDescriptionFile?.file_name ||
                          formik.values.job_description_file_name}
                      </a>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0 ms-2"
                      onClick={() => handleRemoveFile()}
                      title="Delete file"
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Col>

        <Col md={6}>
          <div className="card mb-3 border border-black">
            <div className="card-header">
              <h5 className="mb-0">Additional Documents</h5>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label fw-semibold">
                  Upload Additional File (Max 2MB)
                </label>
                <div className="file-upload-container">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <label
                        htmlFor="additionalFile"
                        className="btn btn-outline-primary"
                        style={{ cursor: "pointer", marginBottom: 0 }}
                      >
                        <i className="fa fa-cloud-upload me-2"></i>
                        Select Files
                      </label>
                      <input
                        id="additionalFile"
                        type="file"
                        onBlur={formik.handleBlur}
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            formik.setFieldValue(
                              "additionalFile",
                              e.target.files[0]
                            );
                          }
                        }}
                        name="additionalFile"
                        className="d-none"
                        accept=".pdf,.doc,.docx"
                      />
                    </div>
                    <div className="small text-muted">
                      Supported formats: PDF, DOC, DOCX
                    </div>
                  </div>
                  {formik.values?.additionalFile && (
                    <div className="selected-files mt-3">
                      <div
                        className="d-flex align-items-center p-2 border rounded mb-2"
                        style={{ backgroundColor: "#f8f9fa" }}
                      >
                        <i
                          className={`fa fa-${
                            formik.values.additionalFile.name
                              .toLowerCase()
                              .endsWith("pdf")
                              ? "file-pdf-o"
                              : "file-word-o"
                          } me-2 text-${
                            formik.values.additionalFile.name
                              .toLowerCase()
                              .endsWith("pdf")
                              ? "danger"
                              : "primary"
                          }`}
                        ></i>
                        <div className="flex-grow-1 ms-2 overflow-hidden">
                          <div className="text-truncate">
                            {formik.values.additionalFile.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0 ms-2"
                          onClick={() => {
                            formik.setFieldValue("additionalFile", null);
                            const fileInput =
                              document.getElementById("additionalFile");
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          style={{ minWidth: "24px" }}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary mt-2"
                        onClick={async () => {
                          const file = await handleUploadFile("additionalFile");
                          if (file) {
                            formik.setFieldValue(
                              "additional_document_file_name",
                              file?.file_name
                            );
                            toast.success("File uploaded successfully");
                          }
                        }}
                        disabled={isUploadingAdditionalFile}
                      >
                        {isUploadingAdditionalFile ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-upload me-2"></i>
                            Upload
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {showFieldError("additionalFile") && (
                    <div className="text-danger mt-2">
                      {formik.errors.additionalFile}
                    </div>
                  )}

                  {!showFieldError("additionalFile") &&
                    formik.values.additionalFile &&
                    formik.errors.additional_document_file_name && (
                      <div className="text-danger mt-2">
                        {formik.errors.additional_document_file_name}
                      </div>
                    )}
                </div>
              </div>
              {(additionalDocumentFile ||
                formik.values.additional_document_file_name) && (
                <div className="form-group mt-2">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <a
                        download
                        className="text-decoration-none text-primary"
                        href={
                          additionalDocumentFile?.file_path ||
                          formik.values.additional_document_file_url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="fa fa-file me-2"></i>
                        {additionalDocumentFile?.file_name ||
                          formik.values.additional_document_file_name}
                      </a>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger p-0 ms-2"
                      onClick={() => handleRemoveAdditionalFile()}
                      title="Delete file"
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};
export default RequirementForm;
